import {createAction, props} from '@ngrx/store';
import {INotification, INotificationChips} from '../models';
import {StockItemUpdateObj} from '../../../shared-utilities/models-old/datastructures';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IAutoOrderOrder} from '../../auto-ordering/models';

//#region GLOBAL

/**
 * Action to set a global error occurrence.
 *
 * This action is dispatched when a global error occurs in the application.
 * It includes the error details to be processed and displayed in the relevant parts of the app.
 *
 * @action
 * @property {IGlobalError} error - The global error object containing details about the error occurrence.
 */
export const setGlobalErrorOccurrence = createAction(
  '[NOTIFICATIONS][Error] Set Global Occurrence',
  props<{
    error: IGlobalError
  }>(),
);

/**
 * ### Action to clear parts of the notification state
 *
 * @action
 * @property {boolean} clearErrors - Whether to clear error state
 * @property {boolean} clearNotifications - Whether to clear the list of notifications
 */
export const clearNotificationState = createAction(
  '[NOTIFICATIONS][Clear] Clear Notification Section',
  props<{
    clearErrors: boolean,
    clearNotifications: boolean
  }>(),
);

/**
 * Action to set a specific property on the notification state.
 *
 * This action is dispatched when a specific property of the notification state needs to be updated.
 * It allows for dynamic modification of the notification state based on the passed property and value.
 *
 * @action
 * @property {string} property - The name of the property to update on the notification state.
 * @property {unknown} value - The new value to assign to the specified property.
 */
export const setPropertyOnNotificationState = createAction(
  '[NOTIFICATIONS][Global] Set Property on Notification State',
  props<{
    property: string;
    value: unknown;
  }>(),
);

/**
 * Action to set the selected notification in the global notifications state.
 *
 * @description
 * This action updates the store with a new selected {@link INotification}, typically
 * when a user selects a notification to view more details.
 *
 * @example
 * dispatch(setSelectedNotification({ notification }));
 *
 * @property notification - The notification to be set as selected.
 */
export const setSelectedNotification = createAction(
  '[NOTIFICATIONS][Global] Set Selected Notification',
  props<{
    notification: INotification
  }>(),
);

//endregion

//#region GET ALL NOTIFICATIONS LIVE CHANGES

/**
 * Action to initiate fetching all live notification changes.
 *
 * @description
 * This action triggers the retrieval of notifications that are streamed live from Firestore.
 * It is typically dispatched after login or when monitoring notification changes in real-time.
 *
 * @action
 * @property {boolean} isLoggingIn - Indicates if the user is currently logging in (used to conditionally filter or initiate the stream).
 * @property {string[]=} storeIds - Optional array of store IDs to filter the notifications stream.
 */
export const getAllNotificationsLiveChanges = createAction(
  '[NOTIFICATIONS][Stream] Get All Notifications Live Changes',
  props<{
    isLoggingIn: boolean,
    storeIds?: string[],
  }>(),
);

/**
 * Action dispatched upon successful retrieval of live notification changes.
 *
 * @description
 * This action carries the notifications retrieved from the Firestore stream, as well as the context
 * in which they were fetched (e.g., if the user was logging in).
 *
 * @action
 * @property {boolean} isLoggingIn - Indicates whether the stream was initialized during login.
 * @property {INotification[]} notifications - The list of notifications received from the live stream.
 * @property {string[]=} storeIds - Optional array of store IDs that were used to filter the notifications.
 */
export const getAllNotificationsLiveChangesSuccess = createAction(
  '[NOTIFICATIONS][Stream] Get All Notifications Live Changes Success',
  props<{
    isLoggingIn: boolean,
    notifications: INotification[],
    storeIds?: string[],
  }>(),
);

//#endregion

//#region GET ALL NOTIFICATIONS PAGED
/**
 * Action to initiate paged retrieval of all notifications.
 *
 * @description
 * This action triggers a paginated fetch of notifications from Firestore. It is typically used
 * when loading older notifications incrementally (e.g., infinite scroll).
 *
 * @action
 * @property {boolean} isLoggingIn - Indicates if the user is currently logging in (can affect paging logic).
 * @property {string[]=} storeIds - Optional array of store IDs used to filter the notification results.
 */
export const getAllNotificationsPaged = createAction(
  '[NOTIFICATIONS][Read] Get All Notifications Paged',
  props<{
    isLoggingIn: boolean,
    storeIds?: string[],
  }>(),
);

/**
 * Action dispatched upon successful paged retrieval of notifications.
 *
 * @description
 * This action carries a list of notifications retrieved via pagination, along with
 * metadata to support further paging (e.g., `lastDocumentId`).
 *
 * @action
 * @property {boolean} isLoggingIn - Indicates whether the fetch was initiated during login.
 * @property {INotification[]} notifications - The list of notifications fetched in the current page.
 * @property {string=} lastDocumentId - Optional ID of the last document fetched, used for next pagination.
 * @property {string[]=} storeIds - Optional store ID filters applied to the query.
 */
export const getAllNotificationsPagedSuccess = createAction(
  '[NOTIFICATIONS][Read] Get All Notifications Paged Success',
  props<{
    isLoggingIn: boolean,
    notifications: INotification[],
    lastDocumentId?: string,
    storeIds?: string[],
  }>(),
);


//#endregion

//#region GET ALL UNREAD NOTIFICATIONS
/**
 * Action to fetch all unread notifications.
 *
 * @description
 * This action initiates the retrieval of unread notifications from Firestore, optionally filtered by store IDs.
 * It is typically used to display badge counts or alert users to unseen activity.
 *
 * @action
 * @property {string[]=} storeIds - Optional array of store IDs used to filter unread notifications.
 */
export const getAllUnreadNotifications = createAction(
  '[NOTIFICATIONS][Stream] Get All Unread Notifications',
  props<{
    storeIds?: string[],
  }>(),
);

/**
 * Action dispatched when unread notifications have been successfully fetched.
 *
 * @description
 * This action contains the array of unread notifications retrieved from Firestore.
 * It supports updating the store state with the latest unread items.
 *
 * @action
 * @property {INotification[]} unreadNotifications - The list of unread notifications.
 * @property {string[]=} storeIds - Optional array of store IDs used to filter unread notifications.
 */
export const getAllUnreadNotificationsSuccessful = createAction(
  '[NOTIFICATIONS][Stream] Get All Unread Notifications Successful',
  props<{
    unreadNotifications: INotification[],
    storeIds?: string[],
  }>(),
);

//#endregion

//#region GET ALL UNSUCCESSFUL UNREAD NOTIFICATIONS
/**
 * Action to fetch all unsuccessful unread notifications.
 *
 * @description
 * This action triggers the retrieval of unread notifications that have a failed status (`success === 'false'`).
 * It is useful for highlighting or troubleshooting failed delivery attempts.
 *
 * @action
 * @property {string[]=} storeIds - Optional array of store IDs used to filter the failed unread notifications.
 */
export const getAllUnsuccessfulUnreadNotifications = createAction(
  '[NOTIFICATIONS][Stream] Get All Unsuccessful Unread Notifications',
  props<{
    storeIds?: string[],
  }>(),
);

/**
 * Action dispatched when unsuccessful unread notifications have been successfully retrieved.
 *
 * @description
 * This action updates the store with unread notifications that failed to process or deliver.
 *
 * @action
 * @property {INotification[]} unreadNotifications - List of unread notifications marked as unsuccessful.
 * @property {string[]=} storeIds - Optional array of store IDs used for filtering.
 */
export const getAllUnsuccessfulUnreadNotificationsSuccessful = createAction(
  '[NOTIFICATIONS][Stream] Get All Unsuccessful Unread Notifications Successful',
  props<{
    unreadNotifications: INotification[],
    storeIds?: string[],
  }>(),
);

//#endregion

//#region UPDATE NOTIFICATION
/**
 * ### Action to initiate the update of a notification
 *
 * Dispatched when a notification update is triggered.
 *
 * @action
 * @property {INotification} notification - The notification object to update
 */
export const updateNotification = createAction(
  '[NOTIFICATIONS][Update] Update Notification',
  props<{
    notification: INotification,
  }>(),
);

/**
 * ### Action dispatched when a notification update succeeds
 *
 * Indicates the notification update was successful.
 */
export const updateNotificationSuccess = createAction(
  '[NOTIFICATIONS][Update] Update Notification Success',
);

/**
 * Action to trigger a batch update for multiple notifications.
 *
 * This action is dispatched to initiate a batch update on a list of notifications. It carries
 * the list of notifications to be updated and a flag indicating whether to merge the changes
 * with existing data or overwrite the data completely.
 *
 * @action
 * @property {INotification[]} notifications - The array of notifications to be updated.
 * @property {boolean} merge - Whether to merge the updated data with existing data (`true`),
 *                             or completely overwrite it (`false`).
 */
export const batchUpdateNotifications = createAction(
  '[NOTIFICATIONS][Update] Batch Update Notifications',
  props<{
    notifications: INotification[],
    merge: boolean,
  }>(),
);

/**
 * Action dispatched when the batch update of notifications succeeds.
 *
 * This action signifies that the batch update operation for the notifications was successful.
 * It does not carry any additional data, as it is simply used to signal success.
 *
 * @action
 */
export const batchUpdateNotificationsSuccess = createAction(
  '[NOTIFICATIONS][Update] Batch Update Notifications Success',
);

//#endregion

//#region GET NOTIFICATION AUTO ORDER
/**
 * ### Action to trigger fetching the auto order details for a given notification
 *
 * @action
 * @property {INotification} notification - The notification for which to retrieve auto order details
 */
export const getNotificationAutoOrder = createAction(
  '[NOTIFICATIONS][Auto Order] Get Notification Auto Order',
  props<{
    notification: INotification,
  }>(),
);

/**
 * ### Action dispatched when auto order details are successfully retrieved
 *
 * @action
 * @property {IAutoOrderOrder} autoOrder - The fetched auto order data
 * @property {INotification} notification - The notification related to the fetched auto order
 */
export const getNotificationAutoOrderSuccess = createAction(
  '[NOTIFICATIONS][Auto Order] Get Notification Auto Order Success',
  props<{
    autoOrder: IAutoOrderOrder,
    notification: INotification,
  }>(),
);

/**
 * ### Action dispatched when fetching auto order details fails
 *
 * @action
 * @property {INotification} notification - The notification related to the failed auto order request
 */
export const getNotificationAutoOrderFailure = createAction(
  '[NOTIFICATIONS][Auto Order] Get Notification Auto Order Failure',
  props<{
    notification: INotification,
  }>(),
);
//#endregion

//#region GET NOTIFICATION STOCK UPDATE
/**
 * ### Action to initiate fetching stock update data for a notification
 *
 * @action
 * @property {INotification} notification - The notification for which the stock update is being fetched
 */
export const getNotificationStockUpdate = createAction(
  '[NOTIFICATIONS][Stock Update] Get Notification Stock Update',
  props<{
    notification: INotification,
  }>(),
);

/**
 * ### Action dispatched on successful stock update retrieval
 *
 * @action
 * @property {INotification} notification - The original notification that triggered the request
 */
export const getNotificationStockUpdateSuccess = createAction(
  '[NOTIFICATIONS][Stock Update] Get Notification Stock Update Success',
  props<{
    notification: INotification,
  }>(),
);

/**
 * Action dispatched to fetch the data for a specific API event that changed stock items.
 *
 * @action
 * @param {string} eventId - The ID of the event in the `api_events` collection in Firebase.
 * @param {string} storeId - The Store Id of the event
 */

export const getStockUpdateChanges = createAction(
  '[NOTIFICATIONS][Stock Update][View Changes] Get Stock Update Changes',
  props<{ eventId: string, storeId: string }>(),
);

/**
 * ### Action dispatched on successful stock changes retrieval
 *
 * @action
 * @property {{[code: string] : StockItemUpdateObj}} changes - the stock changes fetched from firebase
 */
export const getStockUpdateChangesSuccess = createAction(
  '[NOTIFICATIONS][Stock Update][View Changes] Get Stock Update Changes Success',
  props<{
    stockViewChanges: Record<string, StockItemUpdateObj>,
  }>(),
);

/**
 * ### Action to initiate reverting a stock update
 *
 * This action is dispatched when the user chooses to revert stock changes made by an API event.
 * It carries the set of changed stock items that should be reverted in the backend.
 *
 * @action
 * @property {{[code: string] : StockItemUpdateObj}} changes - A map of stock item codes to their original state before the update.
 */
export const revertStockUpdate = createAction(
  '[NOTIFICATIONS][Stock Update][Revert Changes] Revert Stock Update',
  props<{ stockViewChanges: Record<string, StockItemUpdateObj> }>(),
);

export const retryStockUpdate = createAction(
  '[NOTIFICATIONS][Stock Update][Retry Changes] Retry Stock Update',
  props<{ stockViewChanges: Record<string, StockItemUpdateObj> }>(),
);

export const setRetryStockUpdate = createAction(
  '[NOTIFICATIONS][Stock Update] Set Retry stock Update',
  props<{ retryStockUpdate: boolean; excludeFailedBarcodes?: boolean }>(),
);
/**
 * ### Action dispatched when a stock update revert operation completes successfully
 *
 * This signals that the stock data was successfully reverted in the backend.
 * Typically followed by clearing changes from the state or showing a confirmation.
 */
export const revertStockUpdateSuccess = createAction(
  '[NOTIFICATIONS][Stock Update][Revert Changes] Revert Stock Update Success',
);
/**
 * ### Action dispatched when a stock update retry operation completes successfully
 *
 * This signals that the stock data was successfully retried in the backend.
 * Typically followed by clearing changes from the state or showing a confirmation.
 */
export const retryStockUpdateSuccess = createAction(
  '[NOTIFICATIONS][Stock Update][Retry Changes] Retry Stock Update Success',
);


//#endregion

export const setNotificationFilterChips = createAction(
  '[NOTIFICATIONS][Filter] Set Notification Filter Chips',
  props<{ filters: INotificationChips }>(),
);
